import { parseISO } from 'date-fns'
import { useRef } from 'react'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { Size } from '@opallabs/gem'
import { Icon, Token } from '@opallabs/lapidary'
import { isLiveRangeSet, orderedLiveRange } from '../liveRange'
import {
  type CalendarDateRangeFilter,
  type CalendarFilterCategory,
  type CalendarFilterOption,
  type CalendarFilterState,
} from '../types'
import { type UseCalendarFiltersResult } from '../useCalendarFilters'
import { buildValueTree, categoryFilterGroups } from '../valueTree'
import { CategoryTokens, resolveFilterOptions } from './CategoryTokens'
import { FilterCount } from './FilterCount'
import { ModeGroupLabel } from './ModeSelect'
import * as styles from './styles'

/** The two ends of the live window, in the words their tokens wear. */
const LIVE_ENDS = [
  { key: 'from', label: 'Start' },
  { key: 'to', label: 'End' },
] as const

/** How many ends of the window are set, which is how many tokens it has. */
const liveEndCount = (range: CalendarDateRangeFilter): number =>
  LIVE_ENDS.filter(({ key }) => range[key] !== undefined).length

/**
 * The live window under its own row: a token for each end that is set, so
 * either end can be dropped on its own. The ends are read the right way round
 * whichever order they were set in, and dropping one keeps the other.
 */
const LiveRangeTokens: React.FC<{
  locale?: IETFLanguageTag
  onChange: (range: CalendarDateRangeFilter) => void
  /** One end is about to go, named by its token's `data-value`. */
  onRemoving: (valueId: string) => void
  range: CalendarDateRangeFilter
}> = ({ locale, onChange, onRemoving, range }) => {
  const ordered = orderedLiveRange(range)
  const day = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div className={styles.selectors.tokenGroup}>
      <ModeGroupLabel mode="include" />
      <ul className={styles.selectors.tokenList}>
        {LIVE_ENDS.map(({ key, label }) => {
          const value = ordered[key]
          if (value === undefined) return null

          return (
            <li key={key} data-value={`live-${key}`}>
              <Token
                leadingText={label}
                onRemoveToken={() => {
                  onRemoving(`live-${key}`)
                  onChange(
                    Object.fromEntries(
                      Object.entries(ordered).filter(
                        ([end, date]) => end !== key && date !== undefined
                      )
                    )
                  )
                }}
                removable={true}
                text={day.format(parseISO(value))}
                value={`live-${key}`}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}
LiveRangeTokens.displayName = 'Calendar: LiveRangeTokens'

export type CategoryListProps = Readonly<{
  categories: readonly CalendarFilterCategory[]
  /** The filter state, so a row can summarise what is chosen in it. */
  chosen: CalendarFilterState
  locale?: IETFLanguageTag
  onOpen: (categoryId: string) => void
  onRemove: UseCalendarFiltersResult['removeValue']
  onSetLiveRange: (range: CalendarDateRangeFilter) => void
  renderOptionIcon?: (
    category: CalendarFilterCategory,
    option: CalendarFilterOption
  ) => React.ReactNode
  /** Focus comes back to the row a category was opened from. */
  rowRef: (categoryId: string, row: HTMLButtonElement | null) => void
}>

/**
 * Dropping a token takes the button under the keyboard with it, and focus
 * that falls back to the page leaves the drawer. So focus moves on first: to
 * the next token under the category, else the one before it, else the
 * category's own row.
 */
const moveFocusOff = (item: HTMLElement | undefined, valueId: string) => {
  if (item === undefined) return

  const tokens = Array.from(item.querySelectorAll<HTMLElement>('[data-value]'))
  const at = tokens.findIndex((token) => token.dataset.value === valueId)
  const neighbour = tokens[at + 1] ?? tokens[at - 1]
  const target =
    neighbour?.querySelector('button') ??
    item.querySelector<HTMLElement>(`.${styles.selectors.categoryRow}`)
  target?.focus()
}

/**
 * The first pane: one row per category, under it what is chosen in it — grouped
 * by what each group does, so a category matching one label set while it hides
 * another reads as the two things it is.
 */
export const CategoryList: React.FC<CategoryListProps> = ({
  categories,
  chosen: state,
  locale,
  onOpen,
  onRemove,
  onSetLiveRange,
  renderOptionIcon,
  rowRef,
}) => {
  const items = useRef(new Map<string, HTMLElement>())
  const onRemoving = (categoryId: string, valueId: string) =>
    moveFocusOff(items.current.get(categoryId), valueId)

  return (
    <ul className={styles.selectors.categoryList}>
      {categories.map((category) => {
        const chosen = state.cats[category.id]
        const isLive = category.picker === 'dateRange'
        const live = state.live ?? {}
        const tree = buildValueTree(category.options, category.modeScope)
        // What is chosen, split into the groups a person reads it in. A category
        // that makes the choice once has one; Labels has two whenever a set it
        // matches sits beside a set it hides.
        //
        // The tokens below the row are what the number stands for, so it is
        // counted from the same lists: a chosen channel is one token and one. A
        // carve-out is neither — it reads on the token of the row it was carved
        // from, and it is dropped with it.
        const groups =
          chosen === undefined
            ? []
            : categoryFilterGroups(chosen, tree)
                .map((group) => ({
                  carved: resolveFilterOptions(category, group.carved),
                  mode: group.mode,
                  options: resolveFilterOptions(category, group.chosen),
                }))
                .filter((group) => group.options.length > 0)
        const count = isLive
          ? liveEndCount(live)
          : groups.reduce((total, group) => total + group.options.length, 0)

        return (
          <li
            key={category.id}
            ref={(item) => {
              if (item === null) items.current.delete(category.id)
              else items.current.set(category.id, item)
            }}
            data-category={category.id}
          >
            <button
              ref={(row) => rowRef(category.id, row)}
              className={styles.selectors.categoryRow}
              onClick={() => onOpen(category.id)}
              type="button"
            >
              <Icon
                ariaHidden={true}
                glyph={category.icon}
                size={styles.CATEGORY_ICON_SIZE}
              />
              <span className={styles.selectors.text}>{category.label}</span>
              <FilterCount count={count} />
              <span
                aria-hidden={true}
                className={styles.selectors.categoryChevron}
              >
                <Icon
                  ariaHidden={true}
                  className={styles.selectors.lineArrow}
                  glyph="gem-chevron-e"
                  size={Size.Icon}
                />
              </span>
            </button>
            {isLive ? (
              isLiveRangeSet(live) ? (
                <LiveRangeTokens
                  locale={locale}
                  onChange={onSetLiveRange}
                  onRemoving={(valueId) => onRemoving(category.id, valueId)}
                  range={live}
                />
              ) : null
            ) : (
              groups.map((group) => (
                <CategoryTokens
                  key={group.mode}
                  carved={group.carved}
                  category={category}
                  mode={group.mode}
                  onRemove={(valueId) => {
                    onRemoving(category.id, valueId)
                    onRemove(category.id, group.mode, valueId, tree)
                  }}
                  options={group.options}
                  renderOptionIcon={renderOptionIcon}
                  tree={tree}
                />
              ))
            )}
          </li>
        )
      })}
    </ul>
  )
}
CategoryList.displayName = 'Calendar: CategoryList'
